import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  PageHeader,
  Paper,
  Text,
  Group,
  Button,
  Divider,
  Badge,
  Avatar,
  Title,
  BrandLogo,
  ImagePicker,
  Alert,
  Loader,
  BottomDrawer,
  FormField,
  TextInput,
  PhoneInput,
  Switch,
  Modal,
  Tabs,
  detectDefaultPhoneCountry,
  isValidNationalPhone,
  parseStoredPhone,
} from '@vipso/shared';
import { logout, patchOrg, patchUser, patchAuthTheme, setAuthFromInit } from '../store/authSlice';
import type { RootState } from '../store';
import { mapInitAppPayload } from '../store/authUtils';
import { uploadFile } from '../api/uploadFile';
import { useFileViewer } from '../features/files/FileViewerContext';
import {
  useMyOrgBillingQuery,
  useMyOrgInvoicesQuery,
  useUpdateOrgLogoMutation,
  useUpdateOrgProfileMutation,
  useUpdateMyProfileMutation,
  useCreateRazorpayOrderMutation,
  useVerifyRazorpayPaymentMutation,
  useRefreshSessionMutation,
  useListOrgSettingsQuery,
} from '../api/tenantApi';
import { resolveDisplayLogoUrl } from '../utils/resolveDisplayLogoUrl';
import {
  addressToForm,
  EMPTY_ADDRESS_FORM,
  formatAddress,
  type AddressFormValues,
} from '../utils/formatAddress';
import { getStoredLanguage, setStoredLanguage, type AppLanguage } from '../i18n';
import { useState } from 'react';
import { useModuleLabel } from '../hooks/useModuleLabel';
import { useNavAccess } from '../hooks/useNavAccess';
import { CatalogManagement } from '../features/catalog/CatalogManagement';

type RazorpaySuccessResponse = {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
};

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => {
      open: () => void;
      on: (event: string, handler: (response: { error?: { description?: string } }) => void) => void;
    };
  }
}

/** RTK/GraphQL unwrap errors are plain objects — not always `Error`. */
function paymentErrorMessage(err: unknown, fallback: string): string {
  if (typeof err === 'string' && err.trim()) return err;
  if (err instanceof Error && err.message) return err.message;
  if (err && typeof err === 'object') {
    const e = err as {
      message?: unknown;
      error?: unknown;
      errors?: Array<{ message?: unknown }>;
      data?: { errors?: Array<{ message?: unknown }>; error?: unknown };
    };
    const fromGql = e.errors?.[0]?.message ?? e.data?.errors?.[0]?.message;
    if (typeof fromGql === 'string' && fromGql.trim()) return fromGql;
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
    if (typeof e.error === 'string' && e.error.trim()) return e.error;
    if (typeof e.data?.error === 'string' && e.data.error.trim()) return e.data.error;
    try {
      const json = JSON.stringify(err);
      if (json && json !== '{}' && json !== 'null') return json;
    } catch {
      /* ignore */
    }
  }
  return fallback;
}

function loadRazorpayScript(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  return new Promise((resolve) => {
    const existing = document.querySelector('script[data-razorpay]');
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.dataset.razorpay = '1';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Paper
      p="lg"
      radius="md"
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        background: '#fff',
      }}
    >
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={4} c="navy.9">
            {title}
          </Title>
          {description ? (
            <Text size="sm" c="dimmed">
              {description}
            </Text>
          ) : null}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

function MetaRow({ label, value }: { label: string; value?: string | null }) {
  const { t } = useTranslation();
  return (
    <Group justify="space-between" wrap="nowrap" gap="md">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={600} ta="right" style={{ wordBreak: 'break-word' }}>
        {value || t('common.emptyValue')}
      </Text>
    </Group>
  );
}

function AddressFields({
  value,
  onChange,
}: {
  value: AddressFormValues;
  onChange: (next: AddressFormValues) => void;
}) {
  const { t } = useTranslation();
  return (
    <Stack gap="sm">
      <FormField label={t('address.line1')} required>
        <TextInput
          value={value.line1}
          onChange={(e) => onChange({ ...value, line1: e.currentTarget.value })}
        />
      </FormField>
      <FormField label={t('address.line2')}>
        <TextInput
          value={value.line2}
          onChange={(e) => onChange({ ...value, line2: e.currentTarget.value })}
        />
      </FormField>
      <FormField label={t('address.city')} required>
        <TextInput
          value={value.city}
          onChange={(e) => onChange({ ...value, city: e.currentTarget.value })}
        />
      </FormField>
      <FormField label={t('address.state')}>
        <TextInput
          value={value.state}
          onChange={(e) => onChange({ ...value, state: e.currentTarget.value })}
        />
      </FormField>
      <FormField label={t('address.postalCode')}>
        <TextInput
          value={value.postalCode}
          onChange={(e) => onChange({ ...value, postalCode: e.currentTarget.value })}
        />
      </FormField>
    </Stack>
  );
}

function formatInr(n: number) {
  return `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(v?: string | null) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

type OrgInvoice = {
  id: string;
  invoiceNumber: string;
  status: string;
  subtotal: number;
  total: number;
  taxPct: number;
  taxTotal: number;
  grandTotal: number;
  currency: string;
  issuedAt: string;
  dueAt?: string | null;
  paidAt?: string | null;
  markedPaidByAdminId?: string | null;
  notes?: string | null;
  pdfFileId?: string | null;
};

type PaymentTransfer = {
  key: string;
  invoiceId: string;
  invoiceNumber: string;
  amount: number;
  paidAt?: string | null;
  channel: 'razorpay' | 'admin';
  paymentId?: string | null;
  orderId?: string | null;
};

function invoiceGrandTotal(inv: OrgInvoice) {
  const subtotal = Number(inv.total ?? inv.subtotal ?? 0);
  const tax = Number(inv.taxTotal ?? 0);
  return Number(inv.grandTotal ?? subtotal + tax);
}

function parsePaymentTransfers(invoices: OrgInvoice[]): PaymentTransfer[] {
  const transfers: PaymentTransfer[] = [];
  for (const inv of invoices) {
    if (inv.status !== 'paid') continue;
    const notes = String(inv.notes ?? '');
    const payMatch = /razorpay payment=([^\s]+) order=([^\s]+)/.exec(notes);
    const txnMatch = /Transaction id:\s*([^\s\n]+)/i.exec(notes);
    const orderOnly = [...notes.matchAll(/razorpay_order:([^\s\n]+)/g)].map((m) => m[1]);
    const amount = invoiceGrandTotal(inv);

    if (payMatch) {
      transfers.push({
        key: `${inv.id}-rzp-${payMatch[1]}`,
        invoiceId: inv.id,
        invoiceNumber: inv.invoiceNumber,
        amount,
        paidAt: inv.paidAt,
        channel: 'razorpay',
        paymentId: payMatch[1],
        orderId: payMatch[2],
      });
      continue;
    }

    if (txnMatch) {
      transfers.push({
        key: `${inv.id}-rzp-${txnMatch[1]}`,
        invoiceId: inv.id,
        invoiceNumber: inv.invoiceNumber,
        amount,
        paidAt: inv.paidAt,
        channel: 'razorpay',
        paymentId: txnMatch[1],
        orderId: orderOnly.length ? orderOnly[orderOnly.length - 1] : null,
      });
      continue;
    }

    if (orderOnly.length) {
      transfers.push({
        key: `${inv.id}-rzp-order-${orderOnly[orderOnly.length - 1]}`,
        invoiceId: inv.id,
        invoiceNumber: inv.invoiceNumber,
        amount,
        paidAt: inv.paidAt,
        channel: 'razorpay',
        paymentId: null,
        orderId: orderOnly[orderOnly.length - 1],
      });
      continue;
    }

    if (inv.markedPaidByAdminId || inv.paidAt) {
      transfers.push({
        key: `${inv.id}-admin`,
        invoiceId: inv.id,
        invoiceNumber: inv.invoiceNumber,
        amount,
        paidAt: inv.paidAt,
        channel: 'admin',
        paymentId: null,
        orderId: null,
      });
    }
  }
  return transfers.sort((a, b) => {
    const at = a.paidAt ? new Date(a.paidAt).getTime() : 0;
    const bt = b.paidAt ? new Date(b.paidAt).getTime() : 0;
    return bt - at;
  });
}

function InventorySettingsSection() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useListOrgSettingsQuery();
  const settings = data?.listOrgSettings ?? [];
  const strictRow = settings.find((s) => s.code === 'INVENTORY_STRICT_POOL');
  // Default strict when setting missing. Editable only by system admin.
  const strictEnabled = strictRow ? Boolean(strictRow.booleanValue) : true;

  if (isLoading && settings.length === 0) {
    return (
      <Group justify="center" py="sm">
        <Loader size="sm" />
      </Group>
    );
  }
  if (isError) {
    return <Alert color="red" title={t('settings.inventoryUnavailable')} />;
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={600}>
            {t('inventory.strictPool')}
          </Text>
          <Text size="xs" c="dimmed">
            {t('inventory.strictPoolHelp')}
          </Text>
          <Text size="xs" c="dimmed">
            Managed by VIPSo admin.
          </Text>
        </Stack>
        <Switch
          checked={strictEnabled}
          disabled
          aria-label={t('inventory.strictPool')}
        />
      </Group>
    </Stack>
  );
}

function BillingSection() {
  const { t } = useTranslation();
  const { openFileViewer } = useFileViewer();
  const customersLabel = useModuleLabel('customer', 'terminology.customer.plural');
  const vendorsLabel = useModuleLabel('vendor', 'terminology.vendor.plural');
  const productsLabel = useModuleLabel('product', 'terminology.product.plural');
  const { data: billingData, isLoading, isError, refetch: refetchBilling } = useMyOrgBillingQuery();
  const { data: invoicesData, refetch: refetchInvoices } = useMyOrgInvoicesQuery();
  const [createRazorpayOrder, payState] = useCreateRazorpayOrderMutation();
  const [verifyRazorpayPayment, verifyState] = useVerifyRazorpayPaymentMutation();
  const [payingId, setPayingId] = useState<string | null>(null);
  const [paidLocally, setPaidLocally] = useState<Record<string, true>>({});
  const [historyOpen, setHistoryOpen] = useState(false);
  const [feedback, setFeedback] = useState<{
    title: string;
    message: string;
    tone: 'success' | 'error';
  } | null>(null);
  const billing = billingData?.myOrgBilling;
  const invoices = (invoicesData?.myOrgInvoices ?? []) as OrgInvoice[];
  const paymentBusy = payState.isLoading || verifyState.isLoading;

  const unpaidInvoices = invoices.filter(
    (inv) => inv.status === 'pending' && !paidLocally[inv.id],
  );
  const historyInvoices = invoices.filter(
    (inv) => inv.status !== 'pending' || Boolean(paidLocally[inv.id]),
  );
  const paymentTransfers = parsePaymentTransfers(
    invoices.map((inv) =>
      paidLocally[inv.id] && inv.status !== 'paid' ? { ...inv, status: 'paid' } : inv,
    ),
  );
  const hasBillingHistory = historyInvoices.length > 0 || paymentTransfers.length > 0;

  function openInvoicePdf(inv: { id: string; pdfFileId?: string | null }) {
    if (!inv.pdfFileId) {
      setFeedback({
        title: t('settings.downloadInvoice'),
        message: t('settings.pdfGenerating'),
        tone: 'error',
      });
      return;
    }
    openFileViewer({
      fileId: inv.pdfFileId,
      fileName: `invoice-${inv.id}.pdf`,
    });
  }

  async function handlePayNow(invoiceId: string) {
    setPayingId(invoiceId);
    try {
      // 1) Backend creates Razorpay order (Orders API) — never from the browser.
      const result = await createRazorpayOrder({ invoiceId }).unwrap();
      const order = result.createRazorpayOrder;
      if (
        !order?.orderId ||
        !order.keyId ||
        !Number.isFinite(order.amount) ||
        order.amount < 100 ||
        !order.currency
      ) {
        setFeedback({
          title: t('settings.paymentFailedTitle'),
          message: t('settings.invalidCheckoutOrder'),
          tone: 'error',
        });
        setPayingId(null);
        return;
      }

      // 2) Frontend opens Standard Checkout with the server-issued order_id + public key_id.
      const ready = await loadRazorpayScript();
      if (!ready || !window.Razorpay) {
        setFeedback({
          title: t('settings.paymentFailedTitle'),
          message: t('settings.razorpayUnavailable'),
          tone: 'error',
        });
        setPayingId(null);
        return;
      }
      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: order.name,
        description: order.description,
        order_id: order.orderId,
        retry: { enabled: true, max_count: 1 },
        handler: (response: RazorpaySuccessResponse) => {
          void (async () => {
            setPayingId(invoiceId);
            try {
              if (
                !response?.razorpay_payment_id ||
                !response?.razorpay_order_id ||
                !response?.razorpay_signature
              ) {
                throw new Error(t('settings.paymentFailed'));
              }
              // 3) Backend verifies HMAC signature, then marks invoice paid.
              await verifyRazorpayPayment({
                invoiceId,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpayOrderId: response.razorpay_order_id,
                razorpaySignature: response.razorpay_signature,
              }).unwrap();
              setPaidLocally((prev) => ({ ...prev, [invoiceId]: true }));
              void refetchBilling();
              void refetchInvoices();
              setFeedback({
                title: t('settings.paymentReceivedTitle'),
                message: t('settings.paymentReceived'),
                tone: 'success',
              });
            } catch (err) {
              setFeedback({
                title: t('settings.paymentFailedTitle'),
                message: paymentErrorMessage(err, t('settings.paymentFailed')),
                tone: 'error',
              });
            } finally {
              setPayingId(null);
            }
          })();
        },
        modal: {
          ondismiss: () => {
            setPayingId(null);
          },
          confirm_close: true,
        },
      });
      rzp.on('payment.failed', (response) => {
        const description = response?.error?.description || t('settings.paymentFailed');
        setFeedback({
          title: t('settings.paymentFailedTitle'),
          message: description,
          tone: 'error',
        });
        setPayingId(null);
      });
      rzp.open();
    } catch (err) {
      setFeedback({
        title: t('settings.paymentFailedTitle'),
        message: paymentErrorMessage(err, t('settings.paymentFailed')),
        tone: 'error',
      });
      setPayingId(null);
    }
  }

  function renderInvoiceRow(inv: OrgInvoice, opts?: { showPay?: boolean }) {
    const subtotal = Number(inv.total ?? inv.subtotal ?? 0);
    const tax = Number(inv.taxTotal ?? 0);
    const grand = invoiceGrandTotal(inv);
    const isPaid = inv.status === 'paid' || Boolean(paidLocally[inv.id]);
    const canPay = Boolean(opts?.showPay) && inv.status === 'pending' && !paidLocally[inv.id];
    const statusLabel = isPaid && inv.status !== 'paid' ? 'paid' : inv.status;
    const dateLabel = formatDate(inv.paidAt || inv.dueAt || inv.issuedAt);
    return (
      <Stack key={inv.id} gap={4}>
        <Group justify="space-between" wrap="wrap">
          <Stack gap={0}>
            <Text size="sm" fw={600}>
              {inv.invoiceNumber}
            </Text>
            <Text size="xs" c="dimmed">
              {dateLabel} · {statusLabel}
            </Text>
          </Stack>
          <Group gap="sm">
            <Text size="sm" fw={600}>
              {formatInr(grand)}
            </Text>
            {canPay ? (
              <Button
                size="xs"
                loading={paymentBusy && payingId === inv.id}
                onClick={() => void handlePayNow(inv.id)}
              >
                {t('settings.payNow')}
              </Button>
            ) : null}
            {isPaid ? (
              <Button size="xs" variant="light" onClick={() => openInvoicePdf(inv)}>
                {t('settings.downloadInvoice')}
              </Button>
            ) : null}
          </Group>
        </Group>
        <Text size="xs" c="dimmed">
          {t('settings.invoiceBreakup', {
            subtotal: formatInr(subtotal),
            gst: formatInr(tax),
            total: formatInr(grand),
          })}
        </Text>
      </Stack>
    );
  }

  if (isLoading && !billing) return <Loader size="sm" />;
  if (isError || !billing) {
    return (
      <Text size="sm" c="dimmed">
        {t('settings.billingUnavailable')}
      </Text>
    );
  }

  const daysLeft =
    billing.validUntil != null
      ? Math.ceil((new Date(billing.validUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      : null;
  const expiringSoon = daysLeft != null && daysLeft <= 30 && daysLeft >= 0;

  const baseYearly = Number(billing.baseYearly ?? 0);
  const taxPct = Number(billing.taxPct ?? 18);
  const baseGst = Math.round(baseYearly * (taxPct / 100));
  const baseGrand = baseYearly + baseGst;

  const optionalExtras = (billing.addOnCatalogue ?? []).filter(
    (a) => a.isActive && a.key !== 'org.seats',
  );
  const extraSeat = (billing.addOnCatalogue ?? []).find((a) => a.key === 'org.seats');

  return (
    <Stack gap="md">
      {billing.operationsLocked ? (
        <Alert color="red" title={t('settings.operationsLocked')}>
          {t('settings.operationsLockedHint')}
        </Alert>
      ) : null}
      {expiringSoon ? (
        <Alert color="orange" title={t('settings.planExpiring')}>
          {t('settings.planExpiringHint', { days: daysLeft })}
        </Alert>
      ) : null}

      <Stack gap="sm">
        <MetaRow label={t('settings.plan')} value={billing.planName} />
        <MetaRow label={t('common.status')} value={billing.status} />
        <MetaRow label={t('settings.validFrom')} value={formatDate(billing.validFrom)} />
        <MetaRow label={t('settings.validUntil')} value={formatDate(billing.validUntil)} />
        <MetaRow
          label={t('settings.seats')}
          value={`${billing.seatsUsed} / ${billing.seatsAllowed}`}
        />
      </Stack>

      <Divider label={t('settings.planBreakup')} labelPosition="left" />
      <Stack gap={6}>
        <MetaRow
          label={t('settings.basePlanLine')}
          value={`${formatInr(baseYearly)} / ${t('settings.perYear')}`}
        />
        <MetaRow
          label={t('settings.gstLine', { pct: taxPct })}
          value={formatInr(baseGst)}
        />
        <MetaRow
          label={t('settings.grandTotalLine')}
          value={`${formatInr(baseGrand)} / ${t('settings.perYear')}`}
        />
        <Text size="xs" c="dimmed">
          {t('settings.basePlanIncludes', {
            customers: customersLabel,
            vendors: vendorsLabel,
            products: productsLabel,
          })}
        </Text>
      </Stack>

      {billing.seatsUsed >= billing.seatsAllowed ? (
        <Text size="sm" c="dimmed">
          {t('settings.needMoreSeats', {
            price: formatInr(billing.extraSeatYearly),
          })}
        </Text>
      ) : null}

      {billing.activeAddOns.length ? (
        <>
          <Divider label={t('settings.activeAddOns')} labelPosition="left" />
          <Group gap={6}>
            {billing.activeAddOns.map((k) => (
              <Badge key={k} variant="light" color="brand">
                {k === 'entity.approvals' ? t('settings.entityApprovalsName') : k}
              </Badge>
            ))}
          </Group>
        </>
      ) : null}

      <Divider label={t('settings.optionalExtras')} labelPosition="left" />
      <Text size="xs" c="dimmed">
        {t('settings.optionalExtrasHint')}
      </Text>
      <Stack gap="xs">
        {extraSeat ? (
          <Group justify="space-between" wrap="nowrap">
            <Stack gap={0}>
              <Text size="sm" fw={600}>
                {t('settings.extraSeatName')}
              </Text>
              <Text size="xs" c="dimmed">
                {t('settings.extraSeatDesc')}
              </Text>
            </Stack>
            <Text size="sm" fw={600}>
              {formatInr(billing.extraSeatYearly)}/{t('settings.perSeatYear')}
            </Text>
          </Group>
        ) : null}
        {optionalExtras.map((addon) => (
          <Group key={addon.key} justify="space-between" wrap="nowrap">
            <Stack gap={0}>
              <Text size="sm" fw={600}>
                {addon.key === 'entity.approvals'
                  ? t('settings.entityApprovalsName')
                  : addon.name}
                {addon.isSubscribed ? (
                  <Badge size="xs" color="green" style={{ marginLeft: 6 }}>
                    {t('common.active')}
                  </Badge>
                ) : null}
              </Text>
              <Text size="xs" c="dimmed">
                {addon.key === 'entity.approvals'
                  ? t('settings.entityApprovalsDesc', {
                      customers: customersLabel,
                      vendors: vendorsLabel,
                      products: productsLabel,
                    })
                  : addon.description}
              </Text>
            </Stack>
            <Text size="sm" fw={600}>
              {formatInr(addon.yearlyPrice)}/{t('settings.perYear')}
            </Text>
          </Group>
        ))}
      </Stack>

      <Divider label={t('settings.unpaidInvoices')} labelPosition="left" />
      {unpaidInvoices.length === 0 ? (
        <Text size="sm" c="dimmed">
          {invoices.length === 0
            ? t('settings.noInvoices')
            : t('settings.noUnpaidInvoices')}
        </Text>
      ) : (
        <Stack gap="xs">
          {unpaidInvoices.map((inv) => renderInvoiceRow(inv, { showPay: true }))}
        </Stack>
      )}
      {hasBillingHistory ? (
        <Group justify="flex-end">
          <Button size="xs" variant="light" onClick={() => setHistoryOpen(true)}>
            {t('settings.viewBillingHistory')}
          </Button>
        </Group>
      ) : null}

      <Modal
        opened={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={t('settings.billingHistoryTitle')}
        centered
        size="lg"
      >
        <Tabs defaultValue="invoices">
          <Tabs.List>
            <Tabs.Tab value="invoices">{t('settings.invoiceHistory')}</Tabs.Tab>
            <Tabs.Tab value="payments">{t('settings.paymentTransfers')}</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="invoices" pt="md">
            {historyInvoices.length === 0 ? (
              <Text size="sm" c="dimmed">
                {t('settings.noInvoiceHistory')}
              </Text>
            ) : (
              <Stack gap="sm">
                {historyInvoices.map((inv) => renderInvoiceRow(inv))}
              </Stack>
            )}
          </Tabs.Panel>
          <Tabs.Panel value="payments" pt="md">
            {paymentTransfers.length === 0 ? (
              <Text size="sm" c="dimmed">
                {t('settings.noPaymentTransfers')}
              </Text>
            ) : (
              <Stack gap="sm">
                {paymentTransfers.map((tx) => (
                  <Stack key={tx.key} gap={4}>
                    <Group justify="space-between" wrap="wrap">
                      <Stack gap={0}>
                        <Text size="sm" fw={600}>
                          {tx.invoiceNumber}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatDate(tx.paidAt)} ·{' '}
                          {tx.channel === 'razorpay'
                            ? t('settings.paymentViaRazorpay')
                            : t('settings.paymentViaAdmin')}
                        </Text>
                      </Stack>
                      <Text size="sm" fw={600}>
                        {formatInr(tx.amount)}
                      </Text>
                    </Group>
                    {tx.paymentId ? (
                      <Text size="xs" c="dimmed">
                        {t('settings.paymentId')}: {tx.paymentId}
                      </Text>
                    ) : null}
                    {tx.orderId ? (
                      <Text size="xs" c="dimmed">
                        {t('settings.paymentOrderId')}: {tx.orderId}
                      </Text>
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            )}
          </Tabs.Panel>
        </Tabs>
      </Modal>

      <Modal
        opened={Boolean(feedback)}
        onClose={() => setFeedback(null)}
        title={feedback?.title}
        centered
      >
        <Stack gap="md">
          <Text size="sm" c={feedback?.tone === 'success' ? 'green.8' : 'red.8'}>
            {feedback?.message}
          </Text>
          <Group justify="flex-end">
            <Button onClick={() => setFeedback(null)}>{t('common.continue')}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

export function SettingsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [language, setLanguage] = useState<AppLanguage>(() => getStoredLanguage());
  const { user, org, theme, config, tenant, featureKeys, accessToken, refreshToken } =
    useSelector((state: RootState) => state.auth);
  const [updateOrgLogo] = useUpdateOrgLogoMutation();
  const [updateOrgProfile, orgProfileState] = useUpdateOrgProfileMutation();
  const [updateMyProfile, myProfileState] = useUpdateMyProfileMutation();
  const [refreshSession] = useRefreshSessionMutation();
  const displayLogoUrl = resolveDisplayLogoUrl(org, config, theme, {
    tenantSlug: tenant?.slug,
  });
  const nav = useNavAccess();
  const canViewBilling = featureKeys.includes('billing.view');
  const canEditOrg = featureKeys.includes('org.profile.update');
  const canEditProfile = featureKeys.includes('profile.update');
  const showCatalog = nav.categories || nav.units;

  const [orgEditOpen, setOrgEditOpen] = useState(false);
  const [userEditOpen, setUserEditOpen] = useState(false);
  const [orgFormError, setOrgFormError] = useState<string | null>(null);
  const [userFormError, setUserFormError] = useState<string | null>(null);
  const [orgForm, setOrgForm] = useState({
    name: '',
    phone: '',
    phoneCountry: detectDefaultPhoneCountry() as string,
    businessEmail: '',
    contactPersonPhone: '',
    contactPhoneCountry: detectDefaultPhoneCountry() as string,
    gstin: '',
    address: EMPTY_ADDRESS_FORM,
  });
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    address: EMPTY_ADDRESS_FORM,
  });

  function handleLogout() {
    dispatch(logout());
    navigate('/login', { replace: true });
  }

  function handleLanguageChange(lang: AppLanguage) {
    setLanguage(lang);
    setStoredLanguage(lang);
  }

  function openOrgEdit() {
    setOrgFormError(null);
    const phoneParsed = parseStoredPhone(org?.phone);
    const contactParsed = parseStoredPhone(
      org?.contactPersonPhone != null ? String(org.contactPersonPhone) : '',
    );
    setOrgForm({
      name: org?.name ?? '',
      phone: phoneParsed.phone,
      phoneCountry: phoneParsed.country,
      businessEmail: String(org?.businessEmail ?? ''),
      contactPersonPhone: contactParsed.phone,
      contactPhoneCountry: contactParsed.country,
      gstin: String(org?.gstin ?? ''),
      address: addressToForm(org?.address),
    });
    setOrgEditOpen(true);
  }

  function openUserEdit() {
    setUserFormError(null);
    setUserForm({
      name: user?.name ?? '',
      email: user?.email ?? '',
      address: addressToForm(user?.address),
    });
    setUserEditOpen(true);
  }

  async function saveOrgProfile() {
    setOrgFormError(null);
    if (orgForm.phone.trim() && !isValidNationalPhone(orgForm.phone, orgForm.phoneCountry)) {
      setOrgFormError(t('phone.invalid'));
      return;
    }
    if (
      orgForm.contactPersonPhone.trim() &&
      !isValidNationalPhone(orgForm.contactPersonPhone, orgForm.contactPhoneCountry)
    ) {
      setOrgFormError(t('phone.invalid'));
      return;
    }
    try {
      const result = await updateOrgProfile({
        name: orgForm.name.trim(),
        phone: orgForm.phone.trim() || null,
        businessEmail: orgForm.businessEmail.trim() || null,
        contactPersonPhone: orgForm.contactPersonPhone.trim() || null,
        gstin: orgForm.gstin.trim() || null,
        address: {
          line1: orgForm.address.line1.trim(),
          line2: orgForm.address.line2.trim() || undefined,
          city: orgForm.address.city.trim(),
          state: orgForm.address.state.trim() || undefined,
          postalCode: orgForm.address.postalCode.trim() || undefined,
          country: orgForm.address.country || 'IN',
        },
      }).unwrap();
      const updated = result.updateOrgProfile;
      dispatch(
        patchOrg({
          name: updated.name,
          phone: updated.phone ?? undefined,
          businessEmail: updated.businessEmail,
          contactPersonPhone: updated.contactPersonPhone,
          gstin: updated.gstin,
          address: updated.address,
        }),
      );
      setOrgEditOpen(false);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { message?: string } }).data?.message ?? '')
          : '';
      setOrgFormError(message || t('common.saveFailed'));
    }
  }

  async function saveUserProfile() {
    setUserFormError(null);
    try {
      const result = await updateMyProfile({
        name: userForm.name.trim(),
        email: userForm.email.trim() || null,
        address: {
          line1: userForm.address.line1.trim(),
          line2: userForm.address.line2.trim() || undefined,
          city: userForm.address.city.trim(),
          state: userForm.address.state.trim() || undefined,
          postalCode: userForm.address.postalCode.trim() || undefined,
          country: userForm.address.country || 'IN',
        },
      }).unwrap();
      const updated = result.updateMyProfile;
      dispatch(
        patchUser({
          name: updated.name,
          email: updated.email,
          address: updated.address,
        }),
      );
      if (refreshToken) {
        try {
          const refreshed = await refreshSession({ refreshToken }).unwrap();
          dispatch(setAuthFromInit(mapInitAppPayload(refreshed.refreshSession)));
        } catch {
          // local patch already applied
        }
      }
      setUserEditOpen(false);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { message?: string } }).data?.message ?? '')
          : '';
      setUserFormError(message || t('common.saveFailed'));
    }
  }

  return (
    <Stack gap="lg">
      <PageHeader title={t('settings.title')} description={t('settings.description')} />

      <Paper
        p="lg"
        radius="md"
        style={{
          border: '1px solid var(--mantine-color-gray-3)',
          background: 'linear-gradient(135deg, var(--mantine-color-brand-0) 0%, #fff 55%)',
        }}
      >
        <Group justify="space-between" align="center" wrap="wrap">
          <Group gap="md">
            <BrandLogo logoUrl={displayLogoUrl} size={44} withWordmark={false} />
            <Stack gap={2}>
              <Text fw={700} size="lg" c="navy.9">
                {org?.name || t('settings.orgFallback')}
              </Text>
              <Text size="sm" c="dimmed">
                {user?.name || t('settings.userFallback')}
              </Text>
            </Stack>
          </Group>
          <Badge color="brand" variant="light" size="lg">
            {org?.status || t('common.active')}
          </Badge>
        </Group>
      </Paper>

      <BoxGrid>
        <SettingsSection title={t('settings.orgSection')} description={t('settings.orgSectionDesc')}>
          <ImagePicker
            value={org?.logoUrl || null}
            label={t('settings.orgLogo')}
            shape="square"
            size={72}
            disabled={!canEditOrg}
            onUpload={async (file) => {
              if (!canEditOrg) return;
              if (!accessToken) throw new Error(t('auth.notSignedIn'));
              const uploaded = await uploadFile(file, accessToken);
              const result = await updateOrgLogo({ fileId: uploaded.id }).unwrap();
              const logoUrl = result.updateOrgLogo?.logoUrl ?? uploaded.url;
              dispatch(patchOrg({ logoUrl, logoFileId: uploaded.id }));
              dispatch(patchAuthTheme({ logoUrl }));
            }}
            onClear={async () => {
              if (!canEditOrg) return;
              await updateOrgLogo({ fileId: null }).unwrap();
              dispatch(patchOrg({ logoUrl: null, logoFileId: null }));
              dispatch(patchAuthTheme({ logoUrl: null }));
            }}
          />
          <Stack gap="sm">
            <MetaRow label={t('common.name')} value={org?.name} />
            <MetaRow label={t('common.status')} value={org?.status} />
            <MetaRow label={t('common.phone')} value={org?.phone} />
            <MetaRow label={t('settings.uniqueId')} value={org?.uniqueId} />
            <MetaRow label={t('common.address')} value={formatAddress(org?.address)} />
            <MetaRow
              label={t('tasks.businessEmail')}
              value={org?.businessEmail != null ? String(org.businessEmail) : null}
            />
            <MetaRow
              label={t('tasks.contactPhone')}
              value={org?.contactPersonPhone != null ? String(org.contactPersonPhone) : null}
            />
            <MetaRow
              label={t('settings.gstin')}
              value={org?.gstin != null ? String(org.gstin) : null}
            />
          </Stack>
          {canEditOrg ? (
            <Button variant="light" onClick={openOrgEdit}>
              {t('settings.editOrganization')}
            </Button>
          ) : null}
        </SettingsSection>

        <SettingsSection
          title={t('settings.accountSection')}
          description={t('settings.accountSectionDesc')}
        >
          <Group gap="md" mb="sm">
            <Avatar color="brand" radius="xl" size={48}>
              {(user?.name || 'U').slice(0, 1).toUpperCase()}
            </Avatar>
            <Stack gap={0}>
              <Text fw={600}>{user?.name || t('common.emptyValue')}</Text>
              <Text size="sm" c="dimmed">
                {user?.phone || user?.email || t('common.emptyValue')}
              </Text>
            </Stack>
          </Group>
          <Stack gap="sm">
            <MetaRow label={t('common.phone')} value={user?.phone} />
            <MetaRow label={t('common.email')} value={user?.email} />
            <MetaRow label={t('common.address')} value={formatAddress(user?.address)} />
          </Stack>
          {canEditProfile ? (
            <Button variant="light" onClick={openUserEdit}>
              {t('settings.editAccount')}
            </Button>
          ) : null}
        </SettingsSection>

        <SettingsSection title={t('settings.language')} description={t('settings.languageHint')}>
          <Group gap="xs">
            {(
              [
                { value: 'en', label: t('settings.english') },
                { value: 'gu', label: t('settings.gujarati') },
                { value: 'hi', label: t('settings.hindi') },
              ] as const
            ).map((opt) => (
              <Button
                key={opt.value}
                size="sm"
                variant={language === opt.value ? 'filled' : 'default'}
                onClick={() => handleLanguageChange(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </Group>
        </SettingsSection>

        {canViewBilling ? (
          <SettingsSection
            title={t('settings.billingSection')}
            description={t('settings.billingSectionDesc')}
          >
            <BillingSection />
          </SettingsSection>
        ) : null}

        <SettingsSection
          title={t('settings.inventorySection')}
          description={t('settings.inventorySectionDesc')}
        >
          <InventorySettingsSection />
        </SettingsSection>
      </BoxGrid>

      {showCatalog ? (
        <SettingsSection
          title={t('settings.catalogSection')}
          description={t('settings.catalogSectionDesc')}
        >
          <CatalogManagement />
        </SettingsSection>
      ) : null}

      <Divider />

      <Group>
        <Button color="red" variant="light" onClick={handleLogout}>
          {t('auth.signOut')}
        </Button>
      </Group>

      <BottomDrawer
        opened={orgEditOpen}
        onClose={() => setOrgEditOpen(false)}
        title={t('settings.editOrganization')}
      >
        <Stack gap="md">
          {orgFormError ? (
            <Alert color="red" title={t('common.saveFailed')}>
              {orgFormError}
            </Alert>
          ) : null}
          <FormField label={t('common.name')} required>
            <TextInput
              value={orgForm.name}
              onChange={(e) => setOrgForm((f) => ({ ...f, name: e.currentTarget.value }))}
            />
          </FormField>
          <FormField label={t('common.phone')}>
            <PhoneInput
              value={orgForm.phone}
              country={orgForm.phoneCountry}
              countrySearchPlaceholder={t('phone.searchCountry')}
              onChange={({ phone, country }) =>
                setOrgForm((f) => ({ ...f, phone, phoneCountry: country }))
              }
            />
          </FormField>
          <FormField label={t('tasks.businessEmail')}>
            <TextInput
              type="email"
              value={orgForm.businessEmail}
              onChange={(e) =>
                setOrgForm((f) => ({ ...f, businessEmail: e.currentTarget.value }))
              }
            />
          </FormField>
          <FormField label={t('tasks.contactPhone')}>
            <PhoneInput
              value={orgForm.contactPersonPhone}
              country={orgForm.contactPhoneCountry}
              countrySearchPlaceholder={t('phone.searchCountry')}
              onChange={({ phone, country }) =>
                setOrgForm((f) => ({
                  ...f,
                  contactPersonPhone: phone,
                  contactPhoneCountry: country,
                }))
              }
            />
          </FormField>
          <FormField label={t('settings.gstin')}>
            <TextInput
              value={orgForm.gstin}
              maxLength={15}
              onChange={(e) =>
                setOrgForm((f) => ({ ...f, gstin: e.currentTarget.value.toUpperCase() }))
              }
            />
            <Text size="xs" c="dimmed">{t('settings.gstinHint')}</Text>
          </FormField>
          <AddressFields
            value={orgForm.address}
            onChange={(address) => setOrgForm((f) => ({ ...f, address }))}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOrgEditOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={orgProfileState.isLoading}
              disabled={!orgForm.name.trim() || !orgForm.address.line1.trim() || !orgForm.address.city.trim()}
              onClick={() => void saveOrgProfile()}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>

      <BottomDrawer
        opened={userEditOpen}
        onClose={() => setUserEditOpen(false)}
        title={t('settings.editAccount')}
      >
        <Stack gap="md">
          {userFormError ? (
            <Alert color="red" title={t('common.saveFailed')}>
              {userFormError}
            </Alert>
          ) : null}
          <FormField label={t('common.name')} required>
            <TextInput
              value={userForm.name}
              onChange={(e) => setUserForm((f) => ({ ...f, name: e.currentTarget.value }))}
            />
          </FormField>
          <FormField label={t('common.email')}>
            <TextInput
              type="email"
              value={userForm.email}
              onChange={(e) => setUserForm((f) => ({ ...f, email: e.currentTarget.value }))}
            />
          </FormField>
          <AddressFields
            value={userForm.address}
            onChange={(address) => setUserForm((f) => ({ ...f, address }))}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setUserEditOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={myProfileState.isLoading}
              disabled={!userForm.name.trim() || !userForm.address.line1.trim() || !userForm.address.city.trim()}
              onClick={() => void saveUserProfile()}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>
    </Stack>
  );
}

function BoxGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 16,
      }}
    >
      {children}
    </div>
  );
}
